// 領収書発行の API の契約。
//
// 通常版も別注版も wuStorefront の自前の口（front.workup-system.com）を叩く。形は同じで、パスだけが違う。
// （通常版は Issue #82 第2段階、別注版は Issue #97 で旧 OppSites の口から移した）
//
//   通常版（order）
//     GET  /api/receipt-issue?guid=          受注の表示用。無い・キャンセル済みは 404
//     POST /api/receipt-issue/issue          本文 { guid, name, description }。Core2 の内部 API へ中継される
//   別注版（est）
//     GET  /api/est-receipt-issue?guid=      受注の表示用。無ければ 404
//     POST /api/est-receipt-issue/issue      本文 { guid, name, description }。Core2 の内部 API へ中継される
//     GET  /api/est-receipt-issue/pdf?guid=  控えの PDF（ダウンロードのリンク先。estDownloadUrl）
//
// どちらも無認証で、guid を知っていることが実質の資格情報（帳票系と同じ作法）。
// CORS は Access-Control-Allow-Origin: *（IIS の Web.config が付ける）。
//
// 別注版の発行は「発行済みにする」だけで、お客様へのメールは飛ばない。PDF はリンクから取る。

import type { ReceiptKind } from './text'

/** 種類ごとの既定の叩き先。★絶対URLで書くこと（ウィジェットが動くのは店のページの中なので相対だと店を叩く）。 */
export const DEFAULT_API_BASE: Record<ReceiptKind, string> = {
  order: 'https://front.workup-system.com',
  est: 'https://front.workup-system.com',
}

/** 種類ごとの API のパス（apiBase の後ろに付く）。 */
export const API_PATH: Record<ReceiptKind, string> = {
  order: '/api/receipt-issue',
  est: '/api/est-receipt-issue',
}

/**
 * 別注の領収書 PDF のリンク先。読み取りのみで副作用は無い（発行済みでなければ 404）。
 *
 * ★以前は旧 OppSites の `https://www.workup-system.com/DownloadService/EstRecipt/{guid}` に固定していた
 *   （現物の 4 ページはホストが割れて 3 ページ壊れていたので、ウィジェット化のときに固定した）。
 *   Issue #97 で自前の口へ移し、叩き先と同じ apiBase に載せた（ローカル検証では data-api のモックに向く）。
 */
export function estDownloadUrl(guid: string, apiBase: string = DEFAULT_API_BASE.est): string {
  return `${apiBase}${API_PATH.est}/pdf?guid=${encodeURIComponent(guid)}`
}

/**
 * 受注情報の応答。通常版・別注版で同じ項目名。受注が無いときはどちらも 404。
 */
export interface OrderInfo {
  siteName: string
  /** 通常版は数値、別注版は文字列。表示するだけなのでどちらでもよい */
  orderNo: number | string
  /** 会社名＋部署名（無ければ氏名）が入ってくる。画面では編集できる入力欄になる。 */
  name: string
  price: number
  /** 「2026年09月17日」の形で、サーバー側が整形して返す。表示側では組み立て直さない。 */
  date: string
  isIssued: boolean
  mailAddress: string
}

/**
 * 受注情報を引く。
 *
 * 戻り値:
 *   OrderInfo … 引けた
 *   null      … 受注が無い（404）
 *   throw     … 通信エラー・非2xx・JSONとして読めない
 *
 * ★null（受注が無い）と throw（障害）を呼び出し側で分けること。
 *   一緒くたにすると、障害のときに「リンクが古い」という間違った案内を出してしまう。
 */
export async function fetchOrderInfo(
  apiBase: string,
  guid: string,
  kind: ReceiptKind = 'order',
): Promise<OrderInfo | null> {
  const url = `${apiBase}${API_PATH[kind]}?guid=${encodeURIComponent(guid)}`
  const r = await fetch(url, { method: 'GET', cache: 'no-store' })
  if (r.status === 404) return null
  if (!r.ok) throw new Error(`${API_PATH[kind]} GET ${r.status}`)

  const data = (await r.json()) as OrderInfo | null
  // 空文字や 0 ではなく「オブジェクトか」で見る。null も undefined も「受注が無い」に倒す。
  if (data === null || typeof data !== 'object') return null
  return data
}

/** 発行の結果。notFound はウィジェットの「見つかりません」画面に、failed は「エラー」画面に対応する。 */
export type IssueResult = 'issued' | 'notFound' | 'failed'

/**
 * 発行（通常版＝お客様へ領収書メールを送る / 別注版＝発行済みにする）。
 *
 * ★★このリクエストは実際にお客様へメールを送る（別注版でも発行済みになり、ryoshusho に取り消せない登録が残る）。
 *   検証で気軽に叩かないこと。ウィジェットのプレビュー（?wu_receipt_preview=）はここへ一切来ないようにしてある。
 *
 * POST で本文に載せる（宛名を URL のクエリに載せない＝アクセスログに残さない）。
 *
 * 応答（Core2 の状態コードと JSON がそのまま来る）:
 *   200 { issued: true, alreadyIssued, mailSent } … mailSent は通常版だけ。false ならメールだけ失敗している。
 *       客にはメールが届かないので「エラー」を見せる。もう一度押すと Core2 が発行済みとして取り直して再送する
 *   404 … guid が無い（通常版はキャンセル済みも）→ notFound
 *   それ以外（400 / 409 / 502 / 503 など）→ failed
 *
 * 戻り値: 'issued' / 'notFound' / 'failed'。通信エラー・JSON として読めないときは throw。
 */
export async function issueReceipt(
  apiBase: string,
  guid: string,
  name: string,
  description: string,
  kind: ReceiptKind = 'order',
): Promise<IssueResult> {
  const r = await fetch(`${apiBase}${API_PATH[kind]}/issue`, {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ guid, name, description }),
  })
  if (r.status === 404) return 'notFound'
  if (!r.ok) return 'failed'

  const data = (await r.json()) as { issued?: unknown; mailSent?: unknown } | null
  // issued が true で、メールが失敗していない（mailSent が false でない）ときだけ成功。別注版は mailSent が無い。
  return data !== null && data.issued === true && data.mailSent !== false ? 'issued' : 'failed'
}
